/**
 * Audio Loop Player
 * 
 * A loop player for audio files with the same UI/UX as video-loop-player and youtube-loop-player.
 * Breaking change: Now single-song only (no playlist support).
 * 
 * @element audio-loop-player
 * 
 * @prop {string} src - URL to the audio file (required)
 * @prop {string} title - Display title for the audio (optional, defaults to filename)
 * @prop {boolean} showShortcuts - Show keyboard shortcuts help (default: true)
 * @prop {boolean} autoRestore - Restore saved state from localStorage (default: true)
 * @prop {string} storagePrefix - Prefix for storage keys (default: 'audioloop')
 * 
 * @fires loop-created - When a new loop is created
 * @fires loop-deleted - When a loop is deleted
 * @fires playback-state-changed - When play/pause state changes
 */

import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { AudioProcessingService } from '../../services/audio-processing.js';
import { WaveformService } from '../../services/waveform.js';
import { formatTime } from '../../utils/index.js';
import { LoopPlayerMixin, loopPlayerStyles, pauseIcon, playIcon, repeatIcon, seekBackwardIcon, seekForwardIcon } from '../loop-player-base/index.js';

/**
 * Get file basename from path (without extension)
 */
function basename(path: string): string {
  const parts = path.split('/');
  const filename = parts[parts.length - 1] || '';
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
}

@customElement('audio-loop-player')
export class AudioLoopPlayer extends LoopPlayerMixin(LitElement) {
  static styles = [
    ...loopPlayerStyles,
    css`
      /* Audio-specific: compact media wrapper */
      .lp-media-wrapper.audio {
        padding-bottom: 0;
        height: 5rem;
        background: var(--player-panel-bg);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .lp-media-wrapper.audio audio {
        position: static;
        width: 100%;
        height: 2.5rem;
      }

      /* Waveform container */
      .lp-waveform-container {
        width: 100%;
        background: var(--player-panel-bg);
        border-radius: var(--radius-md);
        overflow: hidden;
        margin: var(--space-3) 0;
      }

      /* Hide waveform when not expanded */
      :host(:not([expanded])) .lp-waveform-container,
      :host(:not([expanded])) .lp-waveform-controls,
      :host(:not([expanded])) .lp-waveform-hint {
        display: none;
      }

      /* Waveform controls */
      .lp-waveform-controls {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
      }

      .lp-waveform-controls label {
        font-size: var(--font-size-sm);
        color: var(--player-text-secondary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .lp-waveform-controls input[type="range"] {
        flex: 1;
        max-width: 12.5rem;
        height: var(--slider-track-height);
        cursor: pointer;
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        border: none;
        outline: none;
      }

      .lp-waveform-controls input[type="range"]::-webkit-slider-runnable-track {
        height: var(--slider-track-height);
        background: var(--color-border);
        border-radius: var(--space-1);
      }

      .lp-waveform-controls input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: var(--slider-thumb-size);
        height: var(--slider-thumb-size);
        background: var(--player-accent);
        border-radius: 50%;
        margin-top: calc((var(--slider-track-height) - var(--slider-thumb-size)) / 2);
        cursor: pointer;
        box-shadow: 0 var(--border-width) var(--space-1) rgba(0, 0, 0, 0.2);
      }

      .lp-waveform-controls input[type="range"]::-moz-range-track {
        height: var(--slider-track-height);
        background: var(--color-border);
        border-radius: var(--space-1);
      }

      .lp-waveform-controls input[type="range"]::-moz-range-thumb {
        width: var(--slider-thumb-size);
        height: var(--slider-thumb-size);
        background: var(--player-accent);
        border-radius: 50%;
        border: none;
        cursor: pointer;
        box-shadow: 0 var(--border-width) var(--space-1) rgba(0, 0, 0, 0.2);
      }

      .lp-waveform-hint {
        font-size: var(--font-size-sm);
        color: var(--player-text-secondary);
        padding: var(--space-3);
        text-align: center;
        font-style: italic;
      }

      /* Audio title display */
      .lp-audio-title {
        font-weight: 500;
        font-size: var(--font-size-base);
        color: var(--player-text-primary);
        padding: var(--space-2) 0;
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .lp-audio-title a {
        color: var(--player-accent);
        text-decoration: none;
        font-size: var(--font-size-lg);
      }

      .lp-audio-title a:hover {
        color: var(--player-accent-hover);
      }

      /* Pitch button value display (inside .btn.ghost) */
      .lp-pitch-btn-value {
        font-size: var(--font-size-sm);
        font-weight: 600;
      }

      /* Pitch controls inside native popover */
      .lp-pitch-controls {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }

      .lp-pitch-controls .btn {
        min-width: var(--size-btn-lg);
        height: var(--size-btn-lg);
        font-size: var(--font-size-base);
        font-weight: 600;
        white-space: nowrap;
      }

      .lp-pitch-display {
        font-family: var(--font-mono);
        font-size: var(--font-size-xl);
        font-weight: 600;
        color: var(--player-text-primary);
        min-width: 5rem;
        text-align: center;
        padding: var(--space-2) var(--space-4);
        background: var(--player-surface);
        border-radius: var(--space-1-5);
      }

      .lp-pitch-display.active {
        background: var(--player-accent);
        color: white;
      }

      .lp-pitch-reset-btn {
        width: 100%;
        padding: var(--space-2-5);
        margin-top: var(--space-2);
      }
    `
  ];

  // ─── Public Properties ───────────────────────────────────────────────────

  @property({ type: String })
  src = '';

  @property({ type: String })
  title = '';

  @property({ type: Boolean })
  showShortcuts = true;

  @property({ type: Boolean })
  autoRestore = true;

  @property({ type: String })
  storagePrefix = 'audioloop';

  /** Enable waveform visualization */
  @property({ type: Boolean })
  waveform = false;

  /** Hide the title bar (use when container shows title, e.g., <details><summary>) */
  @property({ type: Boolean })
  compact = false;

  /** Pitch shift in semitones (-12 to +12). Requires SoundTouch processing. */
  @property({ type: Number })
  pitch = 0;

  /** Show pitch control button (advanced feature) */
  @property({ type: Boolean, attribute: 'show-pitch-control' })
  showPitchControl = false;

  // ─── Private State ───────────────────────────────────────────────────────

  @state() private _mediaReady = false;
  @state() private _showPitchPopover = false;

  @query('audio') private _audio!: HTMLAudioElement;
  @query('.lp-waveform-container') private _waveformContainer?: HTMLElement;

  private _waveformService: WaveformService | null = null;
  private _audioProcessingService: AudioProcessingService | null = null;

  @state() private _waveformZoom = 0; // pixels per second (calculated on init)
  @state() private _waveformMinZoom = 0; // minimum zoom to fit entire waveform

  // ─── Abstract Implementation: Compact View ───────────────────────────────

  readonly mediaIcon = '🎵';

  /** Hide expand button for audio player (no waveform yet) */
  get showExpandButton(): boolean {
    // Show expand button when waveform is enabled
    return this.waveform;
  }

  getCompactTitle(): string {
    return this.displayTitle;
  }

  // ─── Computed ────────────────────────────────────────────────────────────

  private get displayTitle(): string {
    return this.title || basename(this.src);
  }

  // ─── Abstract Method Implementations ─────────────────────────────────────

  getMediaId(): string {
    // Use the filename from src as the ID
    const parts = this.src.split('/');
    return parts[parts.length - 1] || this.src;
  }

  play(): void {
    if (this._audioProcessingService && this._audioProcessingService.getMode() === 'soundtouch') {
      this._audioProcessingService.play();
    } else {
      this._audio?.play();
    }
  }

  pause(): void {
    if (this._audioProcessingService && this._audioProcessingService.getMode() === 'soundtouch') {
      this._audioProcessingService.pause();
    } else {
      this._audio?.pause();
    }
  }

  getCurrentTime(): number {
    if (this._audioProcessingService && this._audioProcessingService.getMode() === 'soundtouch') {
      return this._audioProcessingService.getCurrentTime();
    }
    return this._audio?.currentTime ?? 0;
  }

  getDuration(): number {
    return this._audio?.duration ?? 0;
  }

  seekTo(time: number): void {
    if (this._audioProcessingService && this._audioProcessingService.getMode() === 'soundtouch') {
      this._audioProcessingService.seekTo(time);
    } else if (this._audio) {
      this._audio.currentTime = Math.max(0, Math.min(this.duration, time));
    }
  }

  setPlaybackRate(rate: number): void {
    if (this._audioProcessingService) {
      this._audioProcessingService.setPlaybackRate(rate);
    } else if (this._audio) {
      this._audio.playbackRate = rate;
    }
  }

  isMediaReady(): boolean {
    return this._mediaReady;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  firstUpdated() {
    // @query decorator handles the element reference
    if (this._audio) {
      this._setupAudioEvents();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._waveformService?.destroy();
    this._audioProcessingService?.destroy();
  }

  async updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);

    // Reinitialize waveform if src changes while waveform is active
    if (this._waveformService && changedProperties.has('src')) {
      this._waveformService.destroy();
      this._waveformService = null;
      if (this.waveform && this.expanded && this._mediaReady) {
        await this._initWaveform();
      }
    }

    // Initialize waveform when enabled and expanded
    if (this.waveform && this.expanded && this._waveformContainer) {
      // Check if we need to initialize (first time only)
      if (!this._waveformService && this._mediaReady) {
        await this._initWaveform();
        // Sync loops after init (they may have been restored already)
        if (this.loops.length > 0) {
          this._syncLoopsToWaveform();
        }
      } else if (this._waveformService) {
        // Waveform already exists, just sync loops
        this._syncLoopsToWaveform();
      }
    }
    // Don't destroy waveform on collapse - keep it cached for instant re-display

    // Sync loops with waveform regions
    if (this._waveformService && changedProperties.has('loops')) {
      this._syncLoopsToWaveform();
    }

    // Sync active loop highlighting
    if (this._waveformService && changedProperties.has('activeLoopId')) {
      this._waveformService.setActiveRegion(this.activeLoopId);
      this._updateWaveformAutoScroll();
    }

    // Update auto-scroll when loop mode is toggled
    if (this._waveformService && changedProperties.has('loopEnabled')) {
      this._updateWaveformAutoScroll();
    }

    // Update auto-scroll when zoom changes (affects whether loop fits)
    if (this._waveformService && changedProperties.has('_waveformZoom')) {
      this._updateWaveformAutoScroll();
    }

    // Sync pitch changes to AudioProcessingService
    if (changedProperties.has('pitch') && this._audioProcessingService) {
      await this._audioProcessingService.setPitch(this.pitch);
    }
  }

  private async _initWaveform() {
    if (!this._waveformContainer || !this._audio || !this.waveform) return;

    try {
      this._waveformService = new WaveformService();
      await this._waveformService.init({
        container: this._waveformContainer,
        waveColor: 'rgba(128, 128, 128, 0.5)',
        progressColor: 'rgba(58, 166, 255, 0.7)',
        cursorColor: '#3ea6ff',
        height: 100,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        normalize: true,
        enableHover: true,
        enableZoom: true,
        onRegionCreated: (start, end) => this._handleWaveformRegionCreated(start, end),
        onRegionUpdated: (id, start, end) => this._handleWaveformRegionUpdated(id, start, end),
      }, this._audio);

      // AudioProcessingService is now initialized in _setupAudioEvents
      // No need to recreate it here

      // Wait for audio to have duration
      if (!this._audio.duration || this._audio.duration === 0) {
        await new Promise<void>(resolve => {
          const checkDuration = () => {
            if (this._audio && this._audio.duration > 0) {
              resolve();
            } else {
              setTimeout(checkDuration, 50);
            }
          };
          checkDuration();
        });
      }

      // Calculate initial zoom to fit entire waveform
      const duration = this._audio.duration;
      const containerWidth = this._waveformContainer.clientWidth;
      if (duration > 0 && containerWidth > 0) {
        // Calculate pixels per second to fit the entire waveform
        this._waveformMinZoom = Math.floor(containerWidth / duration);
        this._waveformZoom = this._waveformMinZoom;
        // No need to call setZoom as WaveSurfer already shows full waveform by default
      }

      // Sync existing loops as regions
      this._syncLoopsToWaveform();

    } catch (error) {
      console.error('[AudioLoopPlayer] Failed to initialize waveform:', error);

      // Provide more helpful error messages
      if (error instanceof Error) {
        if (error.message.includes('CORS') || error.name === 'NetworkError') {
          console.warn('[AudioLoopPlayer] CORS error: The audio file must be served with "Access-Control-Allow-Origin" header for waveform visualization.');
        } else if (error.name === 'AbortError') {
          console.warn('[AudioLoopPlayer] Failed to load audio for waveform. This may be due to the audio not being ready yet. The player will continue to work without waveform visualization.');
        } else {
          console.warn('[AudioLoopPlayer] Waveform initialization failed. The player will continue to work without waveform visualization.');
        }
      }

      // Cleanup
      this._waveformService?.destroy();
      this._waveformService = null;
    }
  }

  private _handleWaveformRegionCreated(start: number, end: number) {
    // Create a new loop from the waveform selection
    const loop = this.createLoop(start, end);
    this.activateLoop(loop.id);
  }
  private _handleWaveformRegionUpdated(id: string, start: number, end: number) {
    // Update the loop when region is dragged or resized
    const loop = this.loops.find(l => l.id === id);
    if (loop) {
      this.updateLoop(id, { start, end });
    }
  }

  private _handleZoomChange(e: Event) {
    const value = parseInt((e.target as HTMLInputElement).value);
    this._waveformZoom = value;
    this._waveformService?.setZoom(value);
  }

  // ─── Pitch Control Handlers ──────────────────────────────────────────────

  private _togglePitchPopover(e: Event) {
    e.stopPropagation();
    this._showPitchPopover = !this._showPitchPopover;

    if (this._showPitchPopover) {
      this.updateComplete.then(() => {
        const dialog = this.shadowRoot?.querySelector('#pitch-popover') as HTMLDialogElement | null;
        dialog?.showModal?.();
      });
    } else {
      this._closePitchPopover();
    }
  }

  private _closePitchPopover() {
    const dialog = this.shadowRoot?.querySelector('#pitch-popover') as HTMLDialogElement | null;
    dialog?.close?.();
    this._showPitchPopover = false;
  }

  private async _adjustPitch(delta: number) {
    const newPitch = Math.max(-12, Math.min(12, this.pitch + delta));
    // Round to 1 decimal place
    this.pitch = Math.round(newPitch * 10) / 10;

    if (this._audioProcessingService) {
      await this._audioProcessingService.setPitch(this.pitch);
    }

    this.saveCurrentState();
  }

  private async _resetPitch() {
    this.pitch = 0;

    if (this._audioProcessingService) {
      await this._audioProcessingService.setPitch(0);
    }

    this.saveCurrentState();
  }

  private _formatPitchDisplay(semitones: number): string {
    if (semitones === 0) return '0';
    const sign = semitones > 0 ? '+' : '';
    const sharp = semitones > 0 ? '♯' : semitones < 0 ? '♭' : '';
    return `${sign}${semitones} ${sharp}`;
  }

  private _syncLoopsToWaveform() {
    if (!this._waveformService) return;

    // Get current region IDs
    const existingRegionIds = this._waveformService.getRegions().map(r => r.id);
    const loopIds = this.loops.map(l => l.id);

    // Remove regions that no longer exist in loops
    const toRemove = existingRegionIds.filter(id => !loopIds.includes(id));
    toRemove.forEach(id => this._waveformService?.removeRegion(id));

    // Add or update all loops as regions
    this.loops.forEach(loop => {
      this._waveformService?.addOrUpdateRegion({
        id: loop.id,
        start: loop.start,
        end: loop.end,
        color: loop.color,
        label: loop.label,
      });
    });

    // Update auto-scroll behavior based on loop mode
    this._updateWaveformAutoScroll();
  }

  private _updateWaveformAutoScroll() {
    if (!this._waveformService) return;

    // Disable auto-scroll when loop mode is enabled
    this._waveformService.setAutoCenter(!this.loopEnabled);

    // Highlight active loop
    if (this.activeLoopId) {
      this._waveformService.setActiveRegion(this.activeLoopId);
    }
  }

  private _setupAudioEvents() {
    if (!this._audio) return;

    this._audio.addEventListener('loadedmetadata', async () => {
      this._mediaReady = true;
      this.duration = this._audio?.duration ?? 0;
      this.startTimeUpdate();

      // Initialize AudioProcessingService early (not just for waveform)
      if (!this._audioProcessingService) {
        this._audioProcessingService = new AudioProcessingService();
        await this._audioProcessingService.init(this._audio, {
          onTimeUpdate: (time: number) => {
            // Service handles timeupdate when in soundtouch mode
          },
          onEnded: () => {
            // Service handles ended event when in soundtouch mode
          },
        });
      }

      if (this.autoRestore) {
        this.restoreState();

        // If waveform is already initialized and we restored loops, sync them
        if (this._waveformService && this.loops.length > 0) {
          this._syncLoopsToWaveform();
        }
      }
    });

    this._audio.addEventListener('play', () => {
      this.isPlaying = true;
      this.dispatchEvent(new CustomEvent('playback-state-changed', {
        detail: { playing: true },
        bubbles: true,
        composed: true,
      }));
    });

    this._audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this.dispatchEvent(new CustomEvent('playback-state-changed', {
        detail: { playing: false },
        bubbles: true,
        composed: true,
      }));
    });

    this._audio.addEventListener('ended', () => {
      this.isPlaying = false;
    });

    this._audio.addEventListener('error', (e) => {
      console.error('[AudioLoopPlayer] Audio error:', e);
      this._mediaReady = false;
    });
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  // Override to add audio settings button
  _renderControls() {
    return html`
      <div class="lp-controls-row">
        <div class="lp-playback-btns">
          <button class="btn icon square secondary" @click=${() => this.seek(-5)} title="Back 5s" aria-label="Seek backward 5 seconds">
            ${seekBackwardIcon}
          </button>
          ${this.isPlaying
        ? html`<button class="btn icon square" @click=${() => this.togglePlayPause()} title="Pause" aria-label="Pause">
                ${pauseIcon}
              </button>`
        : html`<button class="btn icon square" @click=${() => this.togglePlayPause()} title="Play" aria-label="Play">
                ${playIcon}
              </button>`
      }
          <button class="btn icon square secondary" @click=${() => this.seek(5)} title="Forward 5s" aria-label="Seek forward 5 seconds">
            ${seekForwardIcon}
          </button>
          <button 
            class="btn icon square ${this.loopEnabled ? '' : 'secondary'}" 
            @click=${() => this.toggleLoopEnabled()} 
            title="Toggle loop mode (${this.loopEnabled ? 'ON' : 'OFF'})"
            aria-label="Toggle loop mode"
            aria-pressed="${this.loopEnabled}"
          >
            ${repeatIcon}
          </button>
        </div>
        <span class="time-display">
          ${!this.isMediaReady() && !this.expanded
        ? html`<span class="lp-expand-hint" @click=${() => this.toggleExpanded()} title="Click to expand">▶ Expand</span>`
        : html`${formatTime(this.currentTime)} / ${formatTime(this.duration)}`
      }
        </span>
        <select 
          class="lp-speed-select" 
          .value=${String(this.playbackSpeed)}
          @change=${(e: Event) => this.setSpeed(parseFloat((e.target as HTMLSelectElement).value))}
          title="Playback speed"
        >
          <option value="2">2x</option>
          <option value="1.9">1.9x</option>
          <option value="1.8">1.8x</option>
          <option value="1.7">1.7x</option>
          <option value="1.6">1.6x</option>
          <option value="1.5">1.5x</option>
          <option value="1.4">1.4x</option>
          <option value="1.3">1.3x</option>
          <option value="1.2">1.2x</option>
          <option value="1.1">1.1x</option>
          <option value="1">1x</option>
          <option value="0.9">0.9x</option>
          <option value="0.8">0.8x</option>
          <option value="0.7">0.7x</option>
          <option value="0.6">0.6x</option>
          <option value="0.5">0.5x</option>
          <option value="0.4">0.4x</option>
          <option value="0.3">0.3x</option>
          <option value="0.2">0.2x</option>
        </select>
        ${this.showPitchControl ? html`
          <button 
            class="btn ghost lp-pitch-trigger ${this.pitch !== 0 ? 'active' : ''}"
            @click=${this._togglePitchPopover}
            title="Pitch Control"
            aria-label="Pitch Control"
            aria-expanded="${this._showPitchPopover}"
          >
            ${this.pitch === 0 ? '♯♭' : html`<span class="lp-pitch-btn-value">${this._formatPitchDisplay(this.pitch)}</span>`}
          </button>
        ` : null}
      </div>
    `;
  }

  // All render methods are now in the mixin

  private _renderPitchPopover() {
    return html`
      <dialog 
        id="pitch-popover"
        class="dialog"
        @close=${() => { this._showPitchPopover = false; }}
        @click=${(e: MouseEvent) => {
        if (e.target === (e.currentTarget as HTMLElement)) {
          this._closePitchPopover();
        }
      }}
      >
        <div class="popover-header">
          <span class="popover-title">Pitch Shift</span>
          <button class="popover-close" @click=${() => this._closePitchPopover()}>×</button>
        </div>
        <div class="popover-body">
          <div class="lp-pitch-controls">
            <button 
              class="btn secondary"
              @click=${() => this._adjustPitch(-1)}
              ?disabled=${this.pitch <= -12}
              title="Lower by 1 semitone"
              aria-label="Lower pitch 1 semitone"
            >
              --
            </button>
            <button 
              class="btn secondary"
              @click=${() => this._adjustPitch(-0.1)}
              ?disabled=${this.pitch <= -12}
              title="Lower by 0.1 semitone"
              aria-label="Lower pitch slightly"
            >
              -
            </button>
            <span class="lp-pitch-display ${this.pitch !== 0 ? 'active' : ''}">
              ${this._formatPitchDisplay(this.pitch)}
            </span>
            <button 
              class="btn secondary"
              @click=${() => this._adjustPitch(0.1)}
              ?disabled=${this.pitch >= 12}
              title="Raise by 0.1 semitone"
              aria-label="Raise pitch slightly"
            >
              +
            </button>
            <button 
              class="btn secondary"
              @click=${() => this._adjustPitch(1)}
              ?disabled=${this.pitch >= 12}
              title="Raise by 1 semitone"
              aria-label="Raise pitch 1 semitone"
            >
              ++
            </button>
          </div>

          <button 
            class="btn secondary lp-pitch-reset-btn"
            @click=${this._resetPitch}
            ?disabled=${this.pitch === 0}
          >
            Reset to 0
          </button>

          <p class="popover-hint">
            Fine-tune with +/- (0.1 semitone)<br>
            Jump with ++/-- (1 semitone)
          </p>
        </div>
      </dialog>
    `;
  }

  render() {
    // Simplified DOM structure - audio element is hidden, no media wrapper needed
    return html`
      <div class="lp-container">
        <audio .src=${this.src} preload="metadata" style="display: none;"></audio>
        
        ${this._renderHeader()}
        
        ${this.waveform ? html`
          <div>
            <div class="lp-waveform-container"></div>
            <div class="lp-waveform-controls">
              <label for="zoom-slider">Zoom:</label>
              <input 
                id="zoom-slider"
                type="range" 
                min="${this._waveformMinZoom}" 
                max="500" 
                .value=${String(this._waveformZoom)}
                @input=${this._handleZoomChange}
              />
              <span style="font-size: var(--font-size-sm); color: var(--player-text-secondary);">${this._waveformZoom}px/s</span>
            </div>
            <div class="lp-waveform-hint">
              💡 Tip: Hold <kbd>Shift</kbd> and drag on the waveform to create a new loop
            </div>
          </div>
        ` : null}
        
        <div style="position: relative;">
          ${this._renderControlsAndTimeline()}
          
          ${this.countdown !== null ? html`
            <div class="lp-countdown-standalone" @click=${() => this.cancelCountdown()}>
              <span class="lp-countdown-number">${this.countdown}</span>
            </div>
          ` : null}
        </div>

        <div class="lp-controls-panel">
          ${this._renderEditPanel()}
          ${this._renderShortcuts()}
        </div>
      </div>
      ${this._renderDeleteDialog()}
      ${this._renderPitchPopover()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'audio-loop-player': AudioLoopPlayer;
  }
}
